/**
 * The four floodlight gantries — PENDING.md "The floodlights are unlit geometry."
 *
 * The interface is named `DirectionalLight`, but the shader it feeds is not: `lighting.frag`
 * computes `lightDir = normalize(lightPosition - worldPosition)` and a distance-based
 * `falloff = (max(0, lightRadius - dist) / lightRadius)^2` per fragment (lighting.frag:117-157).
 * That is a point light with an inverse-square-ish falloff. The sun only *reads* as
 * directional because sim/sun.ts places it 10,000 m away with `radius` set to 1,000,000 —
 * large enough next to a hundred-metre stadium that `falloff` never leaves ~1 and the light
 * direction barely changes across the pitch. Nothing about the machinery itself is
 * direction-only, so four ordinary near, finite-radius entries in the same `lights` array are
 * enough to give each gantry a real local pool of light — no new light type or pass needed.
 *
 * Positions were measured, not placed by eye. The stadium's converter merges every source
 * object into one mesh with one primitive per material (`test.glb` has a single node, "test",
 * covering all of the crowd, stands, roof and gantries), so there is no per-object node name
 * left to query — `toDrawItems` in geometry.ts hands back one DrawItem per material, and none
 * of the nine is named for a gantry. Primitive 6 (material "06_lightgrey.png") is the only
 * structural-grey geometry tall enough to be roof or gantry steelwork — it reaches z=39,
 * against 29.1 for the tallest wall primitive — and its vertices above z=34 split into four
 * equal, symmetric, well-separated clusters of 384 each: a real gap in x between -16 and +16 on
 * both long sides, and nothing at all behind either goal line. That gap is what makes these
 * four gantries rather than one continuous roof edge. See `.agent/scratch/gantry-positions.mjs`
 * for the decode, rebuildable from this comment if that file is gone.
 *
 * Centroids, in the simulation's Z-up frame (metres), one per gantry:
 *   (-33.50, +50.07, 37.91)   (+33.50, +50.07, 37.91)
 *   (-33.50, -50.07, 37.91)   (+33.50, -50.07, 37.91)
 *
 * That sits outside PITCH's own grass rim (±60, ±40 — `fullHalfLength`/`fullHalfWidth`) and
 * well above both stand tiers (24.4 m and 29.1 m), which is exactly where a gantry belongs:
 * mounted on the roof edge, set back from the pitch. The `test` node carries no transform of
 * its own (identity — checked against the glTF JSON), so these are the same coordinates the
 * mesh itself is drawn at once `simToRender` applies the sim-to-render rotation, which is the
 * same -90°-about-X rotation `world.rotation` in main.ts applies to the stadium meshes
 * themselves — the light lines up with the geometry it is meant to be lighting.
 */

import { Vector3 } from 'three';
import { simToRender } from '../core/coords';
import type { DirectionalLight } from './deferred';

/** Measured gantry centroids — see the file comment. Simulation frame (Z-up), metres. */
const GANTRY_POSITIONS_SIM: readonly Vector3[] = [
  new Vector3(-33.5, 50.07, 37.91),
  new Vector3(33.5, 50.07, 37.91),
  new Vector3(-33.5, -50.07, 37.91),
  new Vector3(33.5, -50.07, 37.91),
];

/**
 * How far each floodlight's pool of light reaches, in metres — see `lightRadius` in
 * lighting.frag. Chosen, not measured: the nearest pitch point to a gantry is about 40 m away
 * and the farthest corner of the pitch from it is about 129 m, so 130 gives a clearly brighter
 * pool near each gantry's own side (falloff² ≈ 0.48 at 40 m) while the far side of the pitch is
 * left to the two gantries that are actually near it (falloff² ≈ 0.0001 at 129 m) rather than
 * every light contributing evenly everywhere, which would look like a second sun instead of
 * four separate lamps.
 */
export const FLOODLIGHT_RADIUS = 130;

/**
 * A cool white, close in magnitude to the sun's own channels (`sim/sun.ts` runs noon and dusk
 * colours through roughly 0.7 to 1.7 per channel) so four lights blended additively read as a
 * visible fill rather than either vanishing next to the sun or overpowering it. The slight blue
 * lean is the ordinary look of a metal-halide stadium lamp next to daylight.
 */
export const FLOODLIGHT_COLOR = /* @__PURE__ */ new Vector3(1.05, 1.05, 1.15);

/**
 * The four floodlights, ready to append to the render `lights` array.
 *
 * None cast a shadow: `DeferredPipeline` serves shadow maps opt-out, first-come, up to
 * `MAX_SHADOW_MAPS` (4) — leaving these on default would make the pipeline round-robin a
 * shadow map between the sun and whichever three floodlights ask first, for a fill light
 * nobody asked to see a shadow from, at the cost of four more 2048² depth passes every other
 * frame. The sun is the match's one shadow-casting light, and stays that way.
 */
export function floodlights(): DirectionalLight[] {
  return GANTRY_POSITIONS_SIM.map((sim) => ({
    position: simToRender(sim),
    color: FLOODLIGHT_COLOR.clone(),
    radius: FLOODLIGHT_RADIUS,
    castShadow: false,
  }));
}
